"use client";

import { Children, useEffect, useMemo, useRef, useState } from "react";
import Autoplay from "embla-carousel-autoplay";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { scrollToSection } from "@/lib/scroll";
import { capturePostHogEvent } from "@/lib/posthog";

interface BannerMeta {
  id: string;
  title: string;
  link: string;
}

interface HeroCarouselEnhancerProps {
  banners: BannerMeta[];
  locale: string;
  children: React.ReactNode;
}

/**
 * Client-only enhancer that takes server-rendered hero slides and upgrades
 * them into an Embla carousel with autoplay, slide indicators, and PostHog
 * CTA tracking.
 *
 * This module is loaded via `next/dynamic` from HeroCarouselClient so that
 * `embla-carousel-react` and `embla-carousel-autoplay` are code-split out of
 * the homepage initial JS bundle (they stay off the LCP critical path).
 *
 * Does NOT re-render slide content — the children prop contains the full
 * SSR HTML produced by HeroShell / HeroSlide.
 */
export default function HeroCarouselEnhancer({ banners, locale, children }: HeroCarouselEnhancerProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const slides = Children.toArray(children);
  const hasMultipleSlides = slides.length > 1;

  // Detect prefers-reduced-motion — sync initial value so autoplay is
  // configured correctly on the very first Embla init (no subsequent reInit
  // that would visibly rewrite slide transforms right after first paint).
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const heroAutoplayPlugins = useMemo(
    () =>
      hasMultipleSlides
        ? [
            Autoplay({
              delay: reduceMotion ? 999999 : 4000,
              stopOnInteraction: false,
            }),
          ]
        : [],
    [hasMultipleSlides, reduceMotion],
  );

  // Track active slide for progress indicators
  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());
    const handleSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", handleSelect);
    return () => {
      api.off("select", handleSelect);
    };
  }, [api]);

  // Delegated click handler for the server-rendered CTAs.
  // PostHog fires on every hero CTA click regardless of how it's rendered
  // (Link, <a>, or anchor button). Anchor buttons additionally scroll.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handler = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const ctaEl = target.closest<HTMLElement>("[data-hero-cta-id]");
      if (!ctaEl) return;

      const bannerId = ctaEl.dataset.heroCtaId;
      const linkUrl = ctaEl.dataset.heroCtaLink;
      const isAnchor = ctaEl.dataset.heroCtaAnchor === "true";

      if (bannerId) {
        capturePostHogEvent("hero_cta_clicked", {
          banner_id: bannerId,
          link_url: linkUrl,
          locale,
        });
      }

      if (isAnchor && linkUrl && linkUrl.startsWith("#")) {
        e.preventDefault();
        scrollToSection(linkUrl.substring(1));
      }
    };

    container.addEventListener("click", handler);
    return () => container.removeEventListener("click", handler);
  }, [locale]);
  const firstSlide = slides[0] ?? null;

  return (
    <div ref={containerRef} className="relative pt-2">
      <div className="mx-auto max-w-[1536px] aspect-[16/9] lg:aspect-[3/1] px-4 sm:px-6 lg:px-8">
        {hasMultipleSlides ? (
          // Final Embla configuration is applied on mount — no subsequent
          // reInit and no post-paint DOM insertion. This prevents the
          // visible flick the user was seeing ~200–300 ms after first paint
          // and keeps the hero CTA's DOM position stable across hydration.
          <Carousel
            setApi={setApi}
            opts={{
              align: "start",
              loop: true,
              dragFree: false,
              containScroll: "trimSnaps",
            }}
            plugins={heroAutoplayPlugins}
            className="h-full rounded-2xl overflow-hidden"
          >
            <CarouselContent className="h-full">
              {slides.map((slide, index) => (
                <CarouselItem key={banners[index]?.id ?? index} className="h-full">
                  {slide}
                </CarouselItem>
              ))}
            </CarouselContent>

            <div
              role="tablist"
              aria-label="Carousel slides"
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 w-full max-w-[8rem] md:max-w-xs px-4"
            >
              {banners.map((banner, index) => {
                const isActive = current === index;
                return (
                  <button
                    key={banner.id}
                    role="tab"
                    aria-selected={isActive}
                    aria-label={`Go to slide ${index + 1}: ${banner.title}`}
                    onClick={() => api?.scrollTo(index)}
                    className="flex-1 relative h-2 bg-gray-300 dark:bg-gray-700 rounded-full overflow-hidden cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <div
                      key={isActive ? `active-${current}` : `idle-${index}`}
                      className={
                        isActive
                          ? // Full width + origin-left + scaleX(0→1) keeps the
                            // animation on the GPU (compositor-friendly).
                            "absolute inset-y-0 left-0 w-full origin-left bg-white rounded-full motion-safe:scale-x-0 motion-safe:animate-progress-fill motion-reduce:scale-x-100"
                          : "absolute inset-y-0 left-0 w-0 bg-primary rounded-full"
                      }
                    />
                  </button>
                );
              })}
            </div>
          </Carousel>
        ) : (
          <div className="h-full rounded-2xl overflow-hidden">{firstSlide}</div>
        )}
      </div>
    </div>
  );
}
